// @core/stores/payment/PaymentPaymentRouteStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { paymentApi } from '@core/api';
import type {
  CreatePaymentRouteRequest,
  PaymentRoute,
  UpdatePaymentRouteRequest,
} from '@core/types/payment';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class PaymentPaymentRouteStore {
  // List state
  items: PaymentRoute[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: PaymentRoute | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, PaymentRoute>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): PaymentRoute | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: PaymentRoute | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create payment route
   */
  async CreatePaymentRoute(data: CreatePaymentRouteRequest): Promise<PaymentRoute> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await paymentApi.CreatePaymentRoute(data);
      const item = response.payment_route;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete payment route
   */
  async DeletePaymentRoute(id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await paymentApi.DeletePaymentRoute(id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === id) {
          this.currentItem = null;
        }

        this.removeFromCache(id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get payment route by ID
   */
  async GetPaymentRoute(id: string): Promise<PaymentRoute> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await paymentApi.GetPaymentRoute(id);
      const item = response.payment_route;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List payment routes
   */
  async ListPaymentRoutes(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await paymentApi.ListPaymentRoutes(params);

      runInAction(() => {
        this.items = response.payment_routes?.routes ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update payment route
   */
  async UpdatePaymentRoute(id: string, data: UpdatePaymentRouteRequest): Promise<PaymentRoute> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await paymentApi.UpdatePaymentRoute(id, data);
      const item = response.payment_route;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const paymentPaymentRouteStore = new PaymentPaymentRouteStore();
